import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ConfigBlockChrome } from '@/components/authoring'
import { criterionKindLabel, type RubricCriterion } from '@/hooks/use-evaluation-rubrics-admin'

export type CriterionCardChromeProps = {
  row: RubricCriterion
  errors?: { label?: string; key?: string }
  dragHandleProps?: React.HTMLAttributes<HTMLButtonElement>
  setNodeRef?: (node: HTMLElement | null) => void
  style?: React.CSSProperties
  isDragging?: boolean
  onRemove?: () => void
  /** Hide drag handle (L2 canvas — reorder lives on the case file). */
  hideDrag?: boolean
  /** Hide remove (L2 canvas — remove lives on the case file). */
  hideRemove?: boolean
  children: React.ReactNode
  footer?: React.ReactNode
}

/** Rubric criterion shell — wraps shared {@link ConfigBlockChrome}. */
export function CriterionCardChrome({
  row,
  errors,
  dragHandleProps,
  setNodeRef,
  style,
  isDragging = false,
  onRemove,
  hideDrag = false,
  hideRemove = false,
  children,
  footer,
}: CriterionCardChromeProps) {
  const errorText = [errors?.label, errors?.key].filter(Boolean).join(' ')

  return (
    <ConfigBlockChrome
      kindLabel={criterionKindLabel(row.kind)}
      badges={
        row.weight != null && row.kind !== 'requirement' ? (
          <Badge variant="secondary" className="tabular-nums">
            {Math.round((row.weight ?? 0) * 100)}%
          </Badge>
        ) : null
      }
      dragHandleProps={dragHandleProps}
      setNodeRef={setNodeRef}
      style={style}
      isDragging={isDragging}
      onRemove={onRemove}
      hideDrag={hideDrag}
      hideRemove={hideRemove}
      footer={footer}
      errorText={errorText || undefined}
    >
      {children}
    </ConfigBlockChrome>
  )
}

export function CriterionLabelField({
  row,
  error,
  onUpdate,
}: {
  row: RubricCriterion
  error?: string
  onUpdate: (patch: Partial<RubricCriterion>) => void
}) {
  return (
    <div className="space-y-1">
      <Label>Name</Label>
      <Input
        value={row.label}
        onChange={(e) => onUpdate({ label: e.target.value })}
        className={error ? 'border-destructive' : undefined}
        placeholder={row.kind === 'competency' ? 'e.g. Leadership' : 'e.g. Skills fit'}
      />
    </div>
  )
}

export function CriterionDefinitionField({
  row,
  onUpdate,
}: {
  row: RubricCriterion
  onUpdate: (patch: Partial<RubricCriterion>) => void
}) {
  return (
    <div className="space-y-1">
      <Label>What should the AI look for?</Label>
      <textarea
        value={row.definition ?? ''}
        onChange={(e) => onUpdate({ definition: e.target.value })}
        rows={2}
        className="flex min-h-[4rem] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        placeholder={
          row.kind === 'competency'
            ? 'Describe observable behaviours or evidence.'
            : 'How should this be assessed?'
        }
      />
    </div>
  )
}

export function CriterionStableKeyField({
  row,
  error,
  onUpdate,
}: {
  row: RubricCriterion
  error?: string
  onUpdate: (patch: Partial<RubricCriterion>) => void
}) {
  return (
    <div className="space-y-1">
      <Label>Stable key</Label>
      <Input
        value={row.key}
        onChange={(e) => onUpdate({ key: e.target.value })}
        className={`font-mono text-xs ${error ? 'border-destructive' : ''}`}
      />
      <p className="text-xs text-muted-foreground">
        Used in analysis payloads. Prefer leaving the auto-generated value.
      </p>
    </div>
  )
}
